<template>
  <!-- 2026-08-19 t103644 followup: collapse 不再 v-if 移除 DOM（QA/inspector 无法检查 unfilled）。
       改为 ads-baseline 标准的 CSS guard 压塌：shell 留在 DOM，写 data-ads-stage 供检查。 -->
  <div v-if="shouldRenderAd" class="ads-item" :class="{
    'ads-loading': isLoading,
    'ads-item--collapsed': !shouldShowAd && !debugEnabled
  }" :data-ads-stage="adStage" :data-ad-debug-slot="debugLabel || 'unknown'">
    <div class="ads-borded" :class="{ 'ads-borded--debug': debugEnabled }">
      <div v-if="showLabel && isAdFilled" class="ads-borded-title">
        Advertisement
      </div>

      <ins ref="ads" v-bind="adsAttrs" @error="handleAdError" />
    </div>

    <div v-if="debugEnabled" class="ad-msg">
      <div>Slot: {{ debugLabel || 'unknown' }} ({{ adsAttrs['data-ad-slot'] || 'no-slot' }})</div>
      <div v-if="debugPosition">Position: {{ debugPosition }}</div>
      <div>Status: {{ adStatus }}</div>
      <div>Load Time: {{ loadTime }}ms</div>
    </div>
  </div>
</template>

<script>
import { hasRenderableAd } from '@/utils/adConfig'

const AD_STATUS = {
  PENDING: 'pending',
  LOADED: 'loaded',
  FILLED: 'filled',
  UNFILLED: 'unfilled',
  ERROR: 'error',
}

export default {
  name: 'AdComponent',
  props: {
    ads: {
      type: Object,
      default: () => ({}),
    },
    showDebug: {
      type: Boolean,
      default: false,
    },
    forceDebug: {
      type: Boolean,
      default: false,
    },
    debugLabel: {
      type: String,
      default: '',
    },
    debugPosition: {
      type: String,
      default: '',
    },
    timeout: {
      type: Number,
      default: 5000,
    },
    showLabel: {
      type: Boolean,
      default: true,
    },
    minHeight: {
      type: Number,
      default: 100,
    },
    preloadDistance: {
      type: Number,
      default: 300,
    },
    lazy: {
      type: Boolean,
      default: true,
    },
  },

  data () {
    return {
      isAdFilled: true,
      isLoading: true,
      adStatus: AD_STATUS.PENDING,
      loadTime: 0,
      loadStartTime: 0,
      timeoutTimer: null,
      renderTimer: null,
      visibilityTimer: null,
      statusObserver: null,
      lazyObserver: null,
      errorCount: 0,
      maxErrors: 3,
      shouldShowAd: true,
    }
  },

  computed: {
    adStage () {
      // 组件自身状态用 data-ads-stage（AdSense 真值 data-ad-status 归 Google，组件只读不写）
      if (this.adStatus === AD_STATUS.PENDING && this.isLoading) return 'loading'
      if ([AD_STATUS.LOADED, AD_STATUS.FILLED].includes(this.adStatus)) return 'filled'
      if ([AD_STATUS.UNFILLED, AD_STATUS.ERROR].includes(this.adStatus)) return 'collapsed'
      return this.adStatus
    },
    adsAttrs () {
      const attrs = { ...this.ads }
      const classes = ['adsbygoogle']
      if (attrs.class) classes.push(attrs.class)
      attrs.class = classes.join(' ')
      return attrs
    },
    adsenseConfig () {
      return this.$store?.getters?.domainConfig?.adsense || null
    },
    shouldRenderAd () {
      return hasRenderableAd(this.ads)
    },
    componentStyle () {
      return {
        minHeight: `${this.minHeight}px`,
      }
    },
    debugEnabled () {
      // 调试信息仅在显式打开 db=1 时展示，避免线上默认暴露 slot/位置说明
      return this.showDebug || String(this.$route?.query?.db || '') === '1'
    },
  },

  watch: {
    ads: {
      deep: true,
      immediate: true,
      handler (newAds) {
        if (!hasRenderableAd(newAds)) {
          this.shouldShowAd = false
          return
        }
        this.resetState()
        this.scheduleLazyInit()
      },
    },
  },

  mounted () {
    this.scheduleLazyInit()
  },

  beforeDestroy () {
    this.cleanup()
  },

  methods: {
    scheduleLazyInit () {
      if (typeof window === 'undefined' || !this._isMounted || !hasRenderableAd(this.ads)) return
      // 2026-08-19 t103644 followup3: 恢复懒加载（4c65d79 的 no-lazy 是临时线上验证行为，一直未回滚）。
      // lazy prop 默认 true → IntersectionObserver（rootMargin 300px 预载）；显式 :lazy="false" 的位保留立即加载。
      if (!this.lazy) {
        this.$nextTick(() => this.initAd())
        return
      }
      this.$nextTick(() => this.observeLazyAd())
    },

    observeLazyAd () {
      const adsEl = this.$refs.ads
      if (!adsEl || adsEl.getAttribute('data-ad-status')) return

      this.disconnectLazyObserver()

      if (typeof IntersectionObserver === 'undefined') {
        this.initAd()
        return
      }

      this.lazyObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return
          this.disconnectLazyObserver()
          this.initAd()
        })
      }, {
        rootMargin: `${this.preloadDistance}px 0px ${this.preloadDistance}px 0px`,
        threshold: 0,
      })

      this.lazyObserver.observe(adsEl)
    },

    async initAd () {
      if (!this.canRenderAd()) {
        this.shouldShowAd = false
        return
      }

      this.loadStartTime = Date.now()
      this.observeAdStatus()
      this.startTimeoutGuard()

      const loaded = await this.ensureAdScript()
      if (!loaded) {
        this.handleAdError(new Error('script_not_loaded'))
        return
      }
      this.renderAd()
    },

    canRenderAd () {
      return Boolean(this.adsAttrs['data-ad-client'] && this.adsAttrs['data-ad-slot'])
    },

    ensureAdScript () {
      if (typeof window === 'undefined') return Promise.resolve(false)
      if (window.adsbygoogle && Array.isArray(window.adsbygoogle)) return Promise.resolve(true)

      const scriptUrl = this.resolveScriptUrl()
      if (!scriptUrl) return Promise.resolve(false)

      const scriptKey = '__adsenseScriptPromise__'
      if (!window[scriptKey]) {
        window[scriptKey] = this.loadScript(scriptUrl)
      }

      return window[scriptKey]
        .then(() => true)
        .catch(() => {
          window[scriptKey] = null
          return false
        })
    },

    resolveScriptUrl () {
      if (this.adsenseConfig?.scriptUrl) return this.adsenseConfig.scriptUrl
      const client = this.adsAttrs['data-ad-client']
      if (!client) return ''
      return `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${client}`
    },

    loadScript (url) {
      return new Promise((resolve, reject) => {
        const existed = document.querySelector(`script[src="${url}"]`)
        if (existed) {
          if (existed.dataset.loaded === 'true') {
            resolve()
            return
          }
          existed.addEventListener('load', () => resolve(), { once: true })
          existed.addEventListener('error', (error) => reject(error), { once: true })
          return
        }

        const script = document.createElement('script')
        script.src = url
        script.async = true
        script.crossOrigin = 'anonymous'
        script.onload = () => {
          script.dataset.loaded = 'true'
          resolve()
        }
        script.onerror = (error) => reject(error)
        document.head.appendChild(script)
      })
    },

    renderAd (retryCount = 0) {
      const adsEl = this.$refs.ads
      if (!adsEl || adsEl.getAttribute('data-ad-status')) return
      if (!window.adsbygoogle) return

      if (adsEl.offsetWidth <= 0) {
        if (retryCount < this.maxErrors) {
          this.renderTimer = setTimeout(() => this.renderAd(retryCount + 1), 500)
        }
        return
      }

      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({})
      } catch (error) {
        this.handleAdError(error)
      }
    },

    observeAdStatus () {
      const adsEl = this.$refs.ads
      if (!adsEl || typeof MutationObserver === 'undefined') return

      this.disconnectStatusObserver()
      this.statusObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.attributeName === 'data-ad-status') {
            this.updateAdStatus(mutation.target.getAttribute('data-ad-status'))
          }
        })
      })

      this.statusObserver.observe(adsEl, {
        attributes: true,
        attributeFilter: ['data-ad-status'],
      })
      this.updateAdStatus(adsEl.getAttribute('data-ad-status'))
    },

    updateAdStatus (status) {
      const finalStatus = status || AD_STATUS.PENDING
      this.adStatus = finalStatus
      this.isAdFilled = ![AD_STATUS.UNFILLED, AD_STATUS.ERROR].includes(finalStatus)
      this.isLoading = ![AD_STATUS.LOADED, AD_STATUS.FILLED, AD_STATUS.ERROR, AD_STATUS.UNFILLED].includes(finalStatus)

      if ([AD_STATUS.LOADED, AD_STATUS.FILLED].includes(finalStatus)) {
        this.loadTime = Date.now() - this.loadStartTime
        this.clearTimeoutGuard()
        this.scheduleVisibilityGuard()
        this.$emit('adLoaded', {
          status: finalStatus,
          loadTime: this.loadTime,
          adSlot: this.adsAttrs['data-ad-slot'],
        })
      }

      if ([AD_STATUS.ERROR, AD_STATUS.UNFILLED].includes(finalStatus)) {
        this.collapseAd(finalStatus)
      }
    },

    scheduleVisibilityGuard () {
      if (typeof window === 'undefined') return
      this.clearVisibilityGuard()
      this.visibilityTimer = setTimeout(() => this.verifyVisibleAdFrame(), 1200)
    },

    verifyVisibleAdFrame () {
      const adsEl = this.$refs.ads
      if (!adsEl || ![AD_STATUS.LOADED, AD_STATUS.FILLED].includes(this.adStatus)) return
      const frame = adsEl.querySelector('iframe')
      const rect = frame?.getBoundingClientRect()
      if (frame && rect && rect.width > 0 && rect.height > 0) return
      this.collapseAd(AD_STATUS.UNFILLED)
    },

    collapseAd (status) {
      this.adStatus = status
      this.clearTimeoutGuard()
      this.clearVisibilityGuard()
      this.isAdFilled = false
      this.isLoading = false
      this.shouldShowAd = false
      this.$emit('adCollapsed', {
        status,
        adSlot: this.adsAttrs['data-ad-slot'],
      })
    },

    handleAdError (error) {
      this.errorCount += 1

      if (typeof this.$eventrack === 'function') {
        this.$eventrack('ad_load_error', 'error', {
          errorType: error?.message || 'unknown',
          attempt: this.errorCount,
          adSlot: this.adsAttrs['data-ad-slot'],
        })
      }

      if (this.errorCount < this.maxErrors) {
        this.retryAd()
        return
      }

      this.collapseAd(AD_STATUS.ERROR)
      this.$emit('adError', {
        error: error?.message || 'unknown',
        attempts: this.errorCount,
        adSlot: this.adsAttrs['data-ad-slot'],
      })
    },

    retryAd () {
      this.clearTimers()
      this.renderTimer = setTimeout(() => this.initAd(), 1000 * this.errorCount)
    },

    startTimeoutGuard () {
      this.clearTimeoutGuard()
      this.timeoutTimer = setTimeout(() => {
        this.updateAdStatus(AD_STATUS.UNFILLED)
      }, this.timeout)
    },

    clearTimeoutGuard () {
      if (!this.timeoutTimer) return
      clearTimeout(this.timeoutTimer)
      this.timeoutTimer = null
    },

    clearTimers () {
      this.clearTimeoutGuard()
      this.clearVisibilityGuard()
      if (!this.renderTimer) return
      clearTimeout(this.renderTimer)
      this.renderTimer = null
    },

    clearVisibilityGuard () {
      if (!this.visibilityTimer) return
      clearTimeout(this.visibilityTimer)
      this.visibilityTimer = null
    },

    disconnectStatusObserver () {
      if (!this.statusObserver) return
      this.statusObserver.disconnect()
      this.statusObserver = null
    },

    disconnectLazyObserver () {
      if (!this.lazyObserver) return
      this.lazyObserver.disconnect()
      this.lazyObserver = null
    },

    cleanup () {
      this.clearTimers()
      this.disconnectStatusObserver()
      this.disconnectLazyObserver()
    },

    resetState () {
      this.isAdFilled = true
      this.isLoading = true
      this.adStatus = AD_STATUS.PENDING
      this.loadTime = 0
      this.loadStartTime = 0
      this.errorCount = 0
      this.shouldShowAd = true
      this.cleanup()
    },
  },
}
</script>

<style scoped lang="scss">
.ads-loading {
	min-height: 100px;
}

.ads-item {
  position: relative;
  transition: opacity 0.3s;
  box-sizing: border-box;

  &.ads-loading {
    opacity: 0.6;
  }
}

.ads-item--collapsed {
  /* ads-baseline L108: collapse 必须同步写 important guard，覆盖 AdSense/业务可能留下的 inline important 样式 */
  height: 0 !important;
  min-height: 0 !important;
  max-height: 0 !important;
  overflow: hidden !important;
  visibility: hidden !important;
  margin: 0 !important;
  padding: 0 !important;
}

.ads-borded--debug {
	border: 1px dashed #666;
	padding: 8px;
}

.ad-msg {
	position: static;
	margin-top: 6px;
	background: rgba(0, 0, 0, 0.72);
	color: #fff;
	padding: 4px 8px;
	font-size: 12px;
	border-radius: 4px;
	max-width: 100%;
	word-break: break-all;
}
</style>
